import React from 'react';
import { SearchIcon, XIcon, ChevronDownIcon, CheckIcon, CheckCircleIcon, FlameIcon, AlertOctagonIcon, HelpCircleIcon, CircleDotIcon, ShieldIcon } from 'lucide-react';
import { DISPLAY_TYPES, type CaseIssue } from './types';
interface FilterSidebarProps {
  sidebarOpen: boolean;
  typeFilters: Set<string>;
  threadFilter: 'all' | 'terminal' | 'standalone';
  reviewFilter: 'all' | 'reviewed' | 'unreviewed';
  classificationFilter: 'all' | 'hot' | 'critical' | 'tbd' | 'unclassified';
  privilegedFilter: 'all' | 'privileged' | 'not-privileged';
  groupByFamily: boolean;
  hideDuplicates: boolean;
  dateFrom: string;
  dateTo: string;
  participantSearch: string;
  collapsedSections: Set<string>;
  hasActiveFilters: boolean;
  allSenders: string[];
  toggleSection: (section: string) => void;
  toggleTypeFilter: (type: string) => void;
  setThreadFilter: (filter: 'all' | 'terminal' | 'standalone') => void;
  setReviewFilter: (filter: 'all' | 'reviewed' | 'unreviewed') => void;
  setClassificationFilter: (filter: 'all' | 'hot' | 'critical' | 'tbd' | 'unclassified') => void;
  setPrivilegedFilter: (filter: 'all' | 'privileged' | 'not-privileged') => void;
  issueFilter: string | null;
  setIssueFilter: (filter: string | null) => void;
  caseIssues: CaseIssue[];
  docIssues: Record<string, string[]>;
  setGroupByFamily: (v: boolean) => void;
  setHideDuplicates: (v: boolean) => void;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  setParticipantSearch: (v: string) => void;
  commentFilter: 'all' | 'with-comments';
  setCommentFilter: (filter: 'all' | 'with-comments') => void;
  docsWithCommentsCount: number;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  clearAllFilters: () => void;
}
export function FilterSidebar({
  sidebarOpen,
  typeFilters,
  threadFilter,
  reviewFilter,
  classificationFilter,
  privilegedFilter,
  groupByFamily,
  hideDuplicates,
  dateFrom,
  dateTo,
  participantSearch,
  collapsedSections,
  hasActiveFilters,
  allSenders,
  toggleSection,
  toggleTypeFilter,
  setThreadFilter,
  setReviewFilter,
  setClassificationFilter,
  setPrivilegedFilter,
  issueFilter,
  setIssueFilter,
  caseIssues,
  docIssues,
  setGroupByFamily,
  setHideDuplicates,
  setDateFrom,
  setDateTo,
  setParticipantSearch,
  commentFilter,
  setCommentFilter,
  docsWithCommentsCount,
  searchQuery,
  setSearchQuery,
  clearAllFilters
}: FilterSidebarProps) {
  return <div className={`flex flex-col bg-navy-light border-r border-navy-lighter transition-all duration-300 shrink-0 ${sidebarOpen ? 'w-[260px]' : 'w-0 border-r-0'}`} data-id="element-293" data-mp="14ddbf2hl86kg">
      <div className="flex-1 overflow-y-auto overflow-x-hidden" data-id="element-294" data-mp="uhtk6tdyxw21">
        <div className="w-[260px]" data-id="element-295" data-mp="zflm8g8buyjy">
          {/* Search */}
          <div className="p-3 border-b border-navy-lighter" data-id="element-296" data-mp="zg4u1449k6k9">
            <div className="relative" data-id="element-297" data-mp="lh55rxknxtom">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-gray-500" data-id="element-298" data-mp="li6uh4ulqeot">
                <SearchIcon className="w-3.5 h-3.5" data-id="element-299" data-mp="6tdihwodxu6j" />
              </div>
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search documents…" className="w-full bg-navy border border-navy-lighter rounded-lg text-white placeholder-gray-500 text-xs pl-8 pr-7 py-2 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors" data-id="element-300" data-mp="6tn8hk9fk1c8" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-500 hover:text-white" data-id="element-301" data-mp="ks4vffrih9t3">
                  <XIcon className="w-3 h-3" data-id="element-302" data-mp="ukahy9yzuz0w" />
                </button>}
            </div>
          </div>

          {/* Document Type */}
          <div className="border-b border-navy-lighter" data-id="element-303" data-mp="13jny1lhmilul">
            <button onClick={() => toggleSection('type')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-304" data-mp="13l8r16oahwqc">
              Document Type
              <div className="flex items-center gap-2" data-id="element-305" data-mp="37qz4kxuckdlj">
                {typeFilters.size < DISPLAY_TYPES.length && <span className="text-[10px] bg-amber/20 text-amber px-1.5 py-0.5 rounded-full normal-case tracking-normal" data-id="element-306" data-mp="3btdkb92pyx8l">
                    {typeFilters.size} selected
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('type') ? '-rotate-90' : ''}`} data-id="element-307" data-mp="2k1xxbdx938hu" />
              </div>
            </button>
            {!collapsedSections.has('type') && <div className="px-4 pb-4 space-y-2.5" data-id="element-308" data-mp="3ao87ck0j969">
                {DISPLAY_TYPES.map(type => {
              const isChecked = typeFilters.has(type);
              let dotColor = 'bg-gray-500';
              if (type.includes('Email')) dotColor = 'bg-blue-500';
              if (type.includes('Agreement')) dotColor = 'bg-green-500';
              if (type.includes('Report')) dotColor = 'bg-teal-500';
              if (type.includes('Memo')) dotColor = 'bg-gray-400';
              if (type.includes('Invoice')) dotColor = 'bg-amber';
              return <label key={type} className="flex items-center gap-2.5 cursor-pointer group" data-id="element-309" data-mp="3vu6qcqcjca2e">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${isChecked ? 'bg-amber border-amber' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-310" data-mp="3luo8qgk3l0di">
                        {isChecked && <CheckIcon className="w-3 h-3 text-navy" data-id="element-311" data-mp="1g1q0awrhjqjx" />}
                      </div>
                      <div className={`w-2 h-2 rounded-full ${dotColor}`} data-id="element-312" data-mp="39jx7qpvgkdg5" />
                      <span className={`text-sm transition-colors ${isChecked ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-313" data-mp="3ec8cc6158nf1">
                        {type}
                      </span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Review Status */}
          <div className="border-b border-navy-lighter" data-id="element-314" data-mp="14wuv34shbd0">
            <button onClick={() => toggleSection('review')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-315" data-mp="14dj645qfj7f">
              Review Status
              <div className="flex items-center gap-2" data-id="element-316" data-mp="27ubczwhi4ocm">
                {reviewFilter !== 'all' && <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full normal-case tracking-normal" data-id="element-317" data-mp="2cw0rjm3xnk5g">
                    {reviewFilter}
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('review') ? '-rotate-90' : ''}`} data-id="element-318" data-mp="20e6j3wrwv7tj" />
              </div>
            </button>
            {!collapsedSections.has('review') && <div className="px-4 pb-4 space-y-2.5" data-id="element-319" data-mp="1lnz6hxol4j0r">
                {([{
              value: 'all',
              label: 'All documents'
            }, {
              value: 'reviewed',
              label: 'Reviewed'
            }, {
              value: 'unreviewed',
              label: 'Not reviewed'
            }] as const).map(({
              value,
              label
            }) => {
              const isSelected = reviewFilter === value;
              return <label key={value} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setReviewFilter(value)} data-id="element-320" data-mp="hden29er08rs">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-green-400' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-321" data-mp="rc3xxi888syj">
                        {isSelected && <div className="w-2 h-2 rounded-full bg-green-400" data-id="element-322" data-mp="152pgt6xol2wf" />}
                      </div>
                      {value === 'reviewed' && <CheckCircleIcon className="w-3.5 h-3.5 text-green-500" data-id="element-323" data-mp="1f1ewct23ub9i" />}
                      <span className={`text-sm transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-324" data-mp="1j28oxzqhse8q">
                        {label}
                      </span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Classification */}
          <div className="border-b border-navy-lighter" data-id="element-325" data-mp="2pljhif4wzfbm">
            <button onClick={() => toggleSection('classification')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-326" data-mp="2pmunmllu0z5w">
              Classification
              <div className="flex items-center gap-2" data-id="element-327" data-mp="3dgvz2ig5e41h">
                {classificationFilter !== 'all' && <span className="text-[10px] bg-amber/20 text-amber px-1.5 py-0.5 rounded-full normal-case tracking-normal" data-id="element-328" data-mp="38i1fvfe9qdgu">
                    {classificationFilter}
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('classification') ? '-rotate-90' : ''}`} data-id="element-329" data-mp="vcn8agfee1z7" />
              </div>
            </button>
            {!collapsedSections.has('classification') && <div className="px-4 pb-4 space-y-2.5" data-id="element-330" data-mp="158qf3zl72osh">
                {([{
              value: 'all',
              label: 'All',
              icon: null,
              color: 'amber'
            }, {
              value: 'hot',
              label: 'Hot',
              icon: FlameIcon,
              color: 'red-400'
            }, {
              value: 'critical',
              label: 'Critical',
              icon: AlertOctagonIcon,
              color: 'orange-400'
            }, {
              value: 'tbd',
              label: 'TBD',
              icon: HelpCircleIcon,
              color: 'yellow-400'
            }, {
              value: 'unclassified',
              label: 'Unclassified',
              icon: CircleDotIcon,
              color: 'gray-500'
            }] as const).map(({
              value,
              label,
              icon: Icon,
              color
            }) => {
              const isSelected = classificationFilter === value;
              return <label key={value} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setClassificationFilter(value)} data-id="element-331" data-mp="1jxi9ozvghlag">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-amber' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-332" data-mp="19z2ikwwfic0y">
                        {isSelected && <div className="w-2 h-2 rounded-full bg-amber" data-id="element-333" data-mp="3bwmj55905gyy" />}
                      </div>
                      {Icon && <Icon className={`w-3.5 h-3.5 text-${color}`} data-id="element-334" data-mp="37vj069vnr4ti" />}
                      <span className={`text-sm transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-335" data-mp="32x984858ckc4">
                        {label}
                      </span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Privileged */}
          <div className="border-b border-navy-lighter" data-id="element-336" data-mp="3lnh66bu2oklg">
            <button onClick={() => toggleSection('privileged')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-337" data-mp="3lp21i3o7o7kd">
              Privileged
              <div className="flex items-center gap-2" data-id="element-338" data-mp="201r5arhtg3e8">
                {privilegedFilter !== 'all' && <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded-full normal-case tracking-normal" data-id="element-339" data-mp="2a1rswpmpx5o8">
                    {privilegedFilter === 'privileged' ? 'yes' : 'no'}
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('privileged') ? '-rotate-90' : ''}`} data-id="element-340" data-mp="2ita74v03tbeo" />
              </div>
            </button>
            {!collapsedSections.has('privileged') && <div className="px-4 pb-4 space-y-2.5" data-id="element-341" data-mp="2xrvuysk9cxd4">
                {([{
              value: 'all',
              label: 'All documents'
            }, {
              value: 'privileged',
              label: 'Privileged only'
            }, {
              value: 'not-privileged',
              label: 'Non-privileged only'
            }] as const).map(({
              value,
              label
            }) => {
              const isSelected = privilegedFilter === value;
              return <label key={value} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setPrivilegedFilter(value)} data-id="element-342" data-mp="2xu7f3lr6waui">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-red-400' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-343" data-mp="2jwsgbqf1qqpv">
                        {isSelected && <div className="w-2 h-2 rounded-full bg-red-400" data-id="element-344" data-mp="1mhn082uiurpn" />}
                      </div>
                      {value === 'privileged' && <ShieldIcon className="w-3.5 h-3.5 text-red-400" data-id="element-345" data-mp="6patylj5jmwl" />}
                      <span className={`text-sm transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-346" data-mp="3p3seuwcjpeyv">
                        {label}
                      </span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Issues */}
          <div className="border-b border-navy-lighter" data-id="element-347" data-mp="bomco032x2gt">
            <button onClick={() => toggleSection('issues')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-348" data-mp="bo45i8evp3u4">
              Issues
              <div className="flex items-center gap-2" data-id="element-349" data-mp="31ezgyucd4x1m">
                {issueFilter && <span className="text-[10px] px-1.5 py-0.5 rounded-full normal-case tracking-normal" style={{
                backgroundColor: (caseIssues.find(i => i.id === issueFilter)?.color || '#666') + '20',
                color: caseIssues.find(i => i.id === issueFilter)?.color || '#666'
              }} data-id="element-350" data-mp="2has2po77cgsg">
                    {caseIssues.find(i => i.id === issueFilter)?.label}
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('issues') ? '-rotate-90' : ''}`} data-id="element-351" data-mp="1plgmcir0o55o" />
              </div>
            </button>
            {!collapsedSections.has('issues') && <div className="px-4 pb-4 space-y-2.5" data-id="element-352" data-mp="3ez27uc2abkns">
                <label className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setIssueFilter(null)} data-id="element-353" data-mp="3f0d8w44cieb7">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${!issueFilter ? 'border-amber' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-354" data-mp="3rgwjea9lsd3x">
                    {!issueFilter && <div className="w-2 h-2 rounded-full bg-amber" data-id="element-355" data-mp="33labujdbrh8d" />}
                  </div>
                  <span className={`text-sm transition-colors ${!issueFilter ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-356" data-mp="38k4unlbal5sh">
                    All documents
                  </span>
                </label>
                {caseIssues.map(issue => {
              const isSelected = issueFilter === issue.id;
              const count = Object.values(docIssues).filter(ids => ids.includes(issue.id)).length;
              return <label key={issue.id} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setIssueFilter(isSelected ? null : issue.id)} data-id="element-357" data-mp="1dw9mv9gj1rr5">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? '' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} style={isSelected ? {
                  borderColor: issue.color
                } : {}} data-id="element-358" data-mp="21lu6luki8t1h">
                        {isSelected && <div className="w-2 h-2 rounded-full" style={{
                    backgroundColor: issue.color
                  }} data-id="element-359" data-mp="c7xp11xot4so" />}
                      </div>
                      <div className="w-2 h-2 rounded-full shrink-0" style={{
                  backgroundColor: issue.color
                }} data-id="element-360" data-mp="2e3ere2yyfaav" />
                      <span className={`text-sm transition-colors flex-1 ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-361" data-mp="252gltg8vauq6">
                        {issue.label}
                      </span>
                      <span className="text-[10px] text-gray-600" data-id="element-362" data-mp="205q4kplbtt36">{count}</span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Thread Filters */}
          <div className="border-b border-navy-lighter" data-id="element-363" data-mp="3ui6a1o6at36z">
            <button onClick={() => toggleSection('thread')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-364" data-mp="11wydyveohid5">
              Thread Filters
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('thread') ? '-rotate-90' : ''}`} data-id="element-365" data-mp="wxvjg4wbqujf" />
            </button>
            {!collapsedSections.has('thread') && <div className="px-4 pb-4 space-y-2.5" data-id="element-366" data-mp="1pmiy3hmkilo0">
                {(['all', 'terminal', 'standalone'] as const).map(option => {
              const isSelected = threadFilter === option;
              const label = option === 'all' ? 'All emails' : option === 'terminal' ? 'Terminal only' : 'Standalone only';
              return <label key={option} className="flex items-center gap-2.5 cursor-pointer group" data-id="element-367" data-mp="3sv1p25mpjqc8">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-amber' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-368" data-mp="3owc3vqvfh4i1">
                        {isSelected && <div className="w-2 h-2 rounded-full bg-amber" data-id="element-369" data-mp="3a2qwb4lrp8ru" />}
                      </div>
                      <span className={`text-sm transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-370" data-mp="3jver5tt6t2nx">
                        {label}
                      </span>
                    </label>;
            })}
              </div>}
          </div>

          {/* Family View */}
          <div className="border-b border-navy-lighter" data-id="element-371" data-mp="qcmet5ktr0u5">
            <button onClick={() => toggleSection('family')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-372" data-mp="qc48fg4g768i">
              Family View
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('family') ? '-rotate-90' : ''}`} data-id="element-373" data-mp="14397afzzruhs" />
            </button>
            {!collapsedSections.has('family') && <div className="px-4 pb-4" data-id="element-374" data-mp="ge6miza29lj2">
                <label className="flex items-center justify-between cursor-pointer group" data-id="element-375" data-mp="gdezwdyfyy4x">
                  <span className={`text-sm transition-colors ${groupByFamily ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-376" data-mp="lecdsecf560m">
                    Group by family
                  </span>
                  <div className={`w-8 h-4 rounded-full relative transition-colors ${groupByFamily ? 'bg-amber' : 'bg-navy-lighter'}`} data-id="element-377" data-mp="1wuj74v61tzn2">
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${groupByFamily ? 'left-[18px]' : 'left-0.5'}`} data-id="element-378" data-mp="21xtr5hukndd4" />
                  </div>
                </label>
              </div>}
          </div>

          {/* Duplicate Handling */}
          <div className="border-b border-navy-lighter" data-id="element-379" data-mp="1o3sqgik27ga4">
            <button onClick={() => toggleSection('duplicates')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-380" data-mp="1j5r5fs32p6w0">
              Duplicate Handling
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('duplicates') ? '-rotate-90' : ''}`} data-id="element-381" data-mp="38kp7tgwpkvzq" />
            </button>
            {!collapsedSections.has('duplicates') && <div className="px-4 pb-4" data-id="element-382" data-mp="3bliri17jgekt">
                <label className="flex items-center justify-between cursor-pointer group" data-id="element-383" data-mp="3gldg1y3ucs2p">
                  <span className={`text-sm transition-colors ${hideDuplicates ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-384" data-mp="32nf3my92z5pu">
                    Hide duplicates
                  </span>
                  <div className={`w-8 h-4 rounded-full relative transition-colors ${hideDuplicates ? 'bg-amber' : 'bg-navy-lighter'}`} data-id="element-385" data-mp="2svj55m331dl3">
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${hideDuplicates ? 'left-[18px]' : 'left-0.5'}`} data-id="element-386" data-mp="2izz8uyklwcvt" />
                  </div>
                </label>
              </div>}
          </div>

          {/* Date Range */}
          <div className="border-b border-navy-lighter" data-id="element-387" data-mp="2ew9o6k71yfg2">
            <button onClick={() => toggleSection('date')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-388" data-mp="1vareayst8rdq">
              Date Range
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('date') ? '-rotate-90' : ''}`} data-id="element-389" data-mp="20am5l3ghzizb" />
            </button>
            {!collapsedSections.has('date') && <div className="px-4 pb-4 space-y-3" data-id="element-390" data-mp="29a949hhueaoo">
                <div data-id="element-391" data-mp="2nzyioeey4b6w">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-392" data-mp="2o080vftr1e21">
                    From
                  </label>
                  <input type="text" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="YYYY-MM-DD" className="w-full bg-navy border border-navy-lighter rounded-lg text-white placeholder-gray-500 text-sm px-3 py-1.5 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors" data-id="element-393" data-mp="2j26gmrkl78oz" />
                </div>
                <div data-id="element-394" data-mp="1yoab4fg5jay8">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-395" data-mp="1ynr2xqgb00xe">To</label>
                  <input type="text" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="YYYY-MM-DD" className="w-full bg-navy border border-navy-lighter rounded-lg text-white placeholder-gray-500 text-sm px-3 py-1.5 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors" data-id="element-396" data-mp="23g222r37ziow" />
                </div>
              </div>}
          </div>

          {/* Participants */}
          <div className="border-b border-navy-lighter" data-id="element-397" data-mp="o0l4lhh0i3g3">
            <button onClick={() => toggleSection('participants')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-398" data-mp="rzlmmxro1221">
              Participants
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('participants') ? '-rotate-90' : ''}`} data-id="element-399" data-mp="aq9m79vllzha" />
            </button>
            {!collapsedSections.has('participants') && <div className="px-4 pb-4" data-id="element-400" data-mp="13dm7lgq0w190">
                <div className="relative mb-3" data-id="element-401" data-mp="yg3zttnqrs2l">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-gray-500" data-id="element-402" data-mp="yhexrcuia5ls">
                    <SearchIcon className="w-3.5 h-3.5" data-id="element-403" data-mp="pfxnib3s4y50" />
                  </div>
                  <input type="text" value={participantSearch} onChange={e => setParticipantSearch(e.target.value)} placeholder="Search email..." className="w-full bg-navy border border-navy-lighter rounded-lg text-white placeholder-gray-500 text-sm pl-8 pr-3 py-1.5 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors" data-id="element-404" data-mp="pi1puom15p36" />
                  {participantSearch && <button onClick={() => setParticipantSearch('')} className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-500 hover:text-white" data-id="element-405" data-mp="193x9x6uarfy6">
                      <XIcon className="w-3 h-3" data-id="element-406" data-mp="21svv6ctpuy7p" />
                    </button>}
                </div>

                {/* Suggestions */}
                <div className="flex flex-wrap gap-1.5" data-id="element-407" data-mp="1n1vlk4h8gy9z">
                  {allSenders.filter(s => !participantSearch || s.toLowerCase().includes(participantSearch.toLowerCase())).slice(0, 5).map(sender => <button key={sender} onClick={() => setParticipantSearch(sender)} className={`px-2 py-1 rounded text-[10px] transition-colors truncate max-w-full ${participantSearch === sender ? 'bg-amber/20 text-amber border border-amber/30' : 'bg-navy border border-navy-lighter text-gray-400 hover:text-gray-300 hover:bg-navy-hover'}`} title={sender} data-id="element-408" data-mp="45jvzhajic1a">
                        {sender}
                      </button>)}
                </div>
              </div>}
          </div>

          {/* Comments */}
          <div className="border-b border-navy-lighter" data-id="element-409" data-mp="3qecrlx0qrfpd">
            <button onClick={() => toggleSection('comments')} className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold hover:text-gray-300 transition-colors" data-id="element-410" data-mp="1ak2p27iedhqz">
              Comments
              <div className="flex items-center gap-2" data-id="element-411" data-mp="1g6qxwm8fv5ei">
                {commentFilter !== 'all' && <span className="text-[10px] bg-amber/20 text-amber px-1.5 py-0.5 rounded-full normal-case tracking-normal" data-id="element-412" data-mp="1b74bbm6q0ct1">
                    filtered
                  </span>}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${collapsedSections.has('comments') ? '-rotate-90' : ''}`} data-id="element-413" data-mp="23wuptwc9lgpo" />
              </div>
            </button>
            {!collapsedSections.has('comments') && <div className="px-4 pb-4 space-y-2.5" data-id="element-414" data-mp="1p7oqm8dffqhs">
                {[{
              value: 'all' as const,
              label: 'All documents'
            }, {
              value: 'with-comments' as const,
              label: 'With comments'
            }].map(({
              value,
              label
            }) => {
              const isSelected = commentFilter === value;
              return <label key={value} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setCommentFilter(value)} data-id="element-415" data-mp="2u8ocvzrwfdxy">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-amber' : 'border-navy-lighter bg-navy group-hover:border-gray-500'}`} data-id="element-416" data-mp="386mowyiqyyaa">
                        {isSelected && <div className="w-2 h-2 rounded-full bg-amber" data-id="element-417" data-mp="3muzmu11ear7f" />}
                      </div>
                      <span className={`text-sm transition-colors ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`} data-id="element-418" data-mp="3rvdmovsueuud">
                        {label}
                      </span>
                      {value === 'with-comments' && <span className="text-[10px] text-gray-600 ml-auto" data-id="element-419" data-mp="1rte2aun1ixgl">
                          {docsWithCommentsCount}
                        </span>}
                    </label>;
            })}
              </div>}
          </div>
        </div>
      </div>

      {/* Clear All Footer */}
      {hasActiveFilters && <div className="p-4 border-t border-navy-lighter bg-[#0A1520] shrink-0 w-[260px]" data-id="element-420" data-mp="2aj3z1nad5tb5">
          <button onClick={clearAllFilters} className="w-full py-2 text-sm text-amber hover:text-amber-hover transition-colors font-medium" data-id="element-421" data-mp="2oivhq1btrxvh">
            Clear All Filters
          </button>
        </div>}
    </div>;
}